import api from './api';
import { InvoiceListResponse, InvoiceDetailResponse, InvoiceFormData, InvoicePartialPayment, GstReportResponse } from '../types/invoice';
import { ReminderListResponse } from '../types/reminder';

export const invoiceService = {
    // GET /api/site/invoices?page=1&limit=10&...
    list: async (params?: {
        page?: number;
        limit?: number;
        invoiceNumber?: string;
        clientId?: string;
    }): Promise<InvoiceListResponse> => {
        const response = await api.get('invoices', { params });
        return response.data;
    },

    // GET /api/site/invoices/:id
    getById: async (id: string): Promise<InvoiceDetailResponse> => {
        const response = await api.get(`invoices/${id}`);
        return response.data;
    },

    // POST /api/site/invoices
    create: async (data: InvoiceFormData): Promise<InvoiceDetailResponse> => {
        const response = await api.post('invoices', data);
        return response.data;
    },

    // PUT /api/site/invoices/:id
    update: async (id: string, data: Partial<InvoiceFormData>): Promise<InvoiceDetailResponse> => {
        const response = await api.put(`invoices/${id}`, data);
        return response.data;
    },

    // DELETE /api/site/invoices/:id  (soft delete)
    delete: async (id: string): Promise<{ success: boolean; message?: string }> => {
        const response = await api.delete(`invoices/${id}`);
        return response.data;
    },

    // GET /api/site/invoices/sales (fully paid invoices)
    getSales: async (params?: {
        page?: number;
        limit?: number;
    }): Promise<InvoiceListResponse> => {
        const response = await api.get('invoices/sales', { params });
        return response.data;
    },

    // GET /api/site/reminders?page=1&limit=10
    getReminders: async (params?: {
        page?: number;
        limit?: number;
    }): Promise<ReminderListResponse> => {
        const response = await api.get('reminders', { params });
        return response.data;
    },

    // GET /api/site/reminders?dueOnly=true
    getDueReminders: async (): Promise<ReminderListResponse> => {
        const response = await api.get('reminders', { params: { dueOnly: true } });
        return response.data;
    },

    // POST /api/v1/admin/invoices/partial-payment
    partialPayment: async (data: {
        invoiceId: string;
        date: string;
        amount: number;
        accountId: string;
        accountName?: string;
        remark?: string;
    }): Promise<{ success?: boolean; message?: string; data?: any }> => {
        const response = await api.post(`invoices/partial-payment`, data);
        return response.data;
    },

    // GET /api/v1/admin/invoices/:invoiceId/partial-payments
    getPartialPayments: async (invoiceId: string): Promise<{ success?: boolean; message?: string; data?: any; items?: InvoicePartialPayment[] }> => {
        const response = await api.get(`invoices/${invoiceId}/partial-payments`);
        return response.data;
    },

    // PUT /api/v1/admin/invoices/:invoiceId/partial-payments/:paymentId
    updatePartialPayment: async (
        invoiceId: string,
        paymentId: string,
        data: {
            date: string;
            amount: number;
            accountId: string;
            accountName?: string;
            remark?: string;
        }
    ): Promise<{ success?: boolean; message?: string; data?: any }> => {
        const response = await api.put(`invoices/${invoiceId}/partial-payments/${paymentId}`, data);
        return response.data;
    },

    // DELETE /api/v1/admin/invoices/:invoiceId/partial-payments/:paymentId
    deletePartialPayment: async (
        invoiceId: string,
        paymentId: string
    ): Promise<{ success?: boolean; message?: string; data?: any }> => {
        const response = await api.delete(`invoices/${invoiceId}/partial-payments/${paymentId}`);
        return response.data;
    },

    // POST /api/site/invoices/move-to-sale
    moveToSale: async (data: {
        invoiceId: string;
        type: 'loss' | 'tds';
        amount: number;
        date: string;
        remark?: string;
    }): Promise<{ success?: boolean; message?: string; data?: any }> => {
        const response = await api.post('invoices/move-to-sale', data);
        return response.data;
    },

    // POST /api/site/invoices/:invoiceId/revert-move-to-sale
    revertMoveToSale: async (invoiceId: string): Promise<{ success?: boolean; message?: string; data?: any }> => {
        const response = await api.post(`invoices/${invoiceId}/revert-move-to-sale`);
        return response.data;
    },

    // GET /api/site/invoices/gst-report
    getGstReport: async (params?: {
        fromDate?: string;
        toDate?: string;
        financialYear?: string;
        page?: number;
        limit?: number;
    }): Promise<GstReportResponse> => {
        const response = await api.get('invoices/gst-report', { params });
        return response.data;
    },
    getNextNumber: async (type: string, date: string): Promise<{ success: boolean; data: { nextNumber: string } }> => {
        const response = await api.get('invoices/next-number', { params: { type, date } });
        return response.data;
    },

    getAllInvoicesWithAllStatus: async (clientId?: string): Promise<any> => {
        const response = await api.get('invoices/all', { params: { clientId } });
        return response.data;
    },

    getCreditedPayments: async (clientId?: string): Promise<any> => {
        const response = await api.get('invoices/credited', { params: { clientId } });
        return response.data;
    },

    getClientStatistics: async (clientId: string): Promise<any> => {
        const response = await api.get(`invoices/client-statistics/${clientId}`);
        return response.data;
    },
};
